import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { companyService } from "../../services/company";
import { currentService } from "../../services/current";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";
import { Toolbar } from "primereact/toolbar";
import CompanyForm from "../../components/forms/CompanyForm";
import ChangePasswordForm from "../../components/forms/ChangePasswordForm";
import UserClaimForm from "../../components/forms/UserClaimForm";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import PasswordDialog from "../../components/ui/PasswordDialog";

export default function CompanyManagement() {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [claimDialog, setClaimDialog] = useState(false);
  const [passInfoDialog, setPassInfoDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const queryClient = useQueryClient();

  const { data: users, isLoading } = useQuery({
    queryKey: ["Users"],
    queryFn: () => companyService.getCompanyUsers(),
  });
  const { data: currents } = useQuery({
    queryKey: ["Currents"],
    queryFn: () => currentService.getCurrents(),
  });

  const mutation = useMutation({
    mutationFn: (userData) => {
      return selectedUser
        ? companyService.updateUser(userData)
        : companyService.createCompanyUser(userData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Users"] });
      toast.success(
        selectedUser ? "Kullanıcı güncellendi" : "Kullanıcı eklendi"
      );
      handleCloseDialog();
    },
    onError: () => {
      toast.error("Bir hata oluştu");
    },
  });
  const passwordMutation = useMutation({
    mutationFn: (userData) => {
      return companyService.updateUserPassword(userData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Users"] });
      toast.success("Kullanıcı şifresi güncellendi");
      closePasswordDialog();
    },
    onError: () => {
      toast.error("Bir hata oluştu");
    },
  });
  const claimMutation = useMutation({
    mutationFn: (userData) => {
      return companyService.updateUserClaim(userData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Users"] });
      toast.success("Kullanıcı yetkisi güncellendi");
      closeClaimDialog();
    },
    onError: () => {
      toast.error("Bir hata oluştu");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId) => companyService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Users"] });
      toast.success("Kullanıcı silindi");
    },
    onError: () => {
      toast.error("Kullanıcı silinirken bir hata oluştu");
    },
  });

  const openDialog = (type, user) => {
    setSelectedUser(user);
    if (type === "password") setPasswordDialog(true);
    else if (type === "claim") setClaimDialog(true);
    else if (type === "passInfo") setPassInfoDialog(true);
    else setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedUser(null);
    setDialogOpen(false);
  };

  const closePasswordDialog = () => {
    setSelectedUser(null);
    setPasswordDialog(false);
  };

  const closeClaimDialog = () => {
    setSelectedUser(null);
    setClaimDialog(false);
  };
  const closePassInfoDialog = () => {
    setSelectedUser(null);
    setPassInfoDialog(false);
  };

  const handleDeleteConfirmation = (userId) => {
    confirmDialog({
      message: "Bu kullanıcıyı silmek istediğinizden emin misiniz?",
      header: "Kullanıcı Sil",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      icon: "pi pi-exclamation-triangle",
      accept: () => deleteMutation.mutate(userId),
    });
  };
  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-1">
        <React.Fragment>
          <Button
            icon="pi pi-print"
            text
            onClick={() => openDialog("passInfo", rowData)}
          />
          <Button
            icon="pi pi-crown"
            text
            onClick={() => openDialog("claim", rowData)}
          />
          <Button
            icon="pi pi-key"
            text
            onClick={() => openDialog("password", rowData)}
          />
          <Button
            icon="pi pi-pencil"
            text
            onClick={() => openDialog("bos", rowData)}
          />
          <Button
            text
            icon="pi pi-trash"
            className="p-button-danger"
            onClick={() => handleDeleteConfirmation(rowData.userId)}
          />
        </React.Fragment>
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => {
    return <span>{rowData.status ? "Aktif" : "Pasif"}</span>;
  };
  const headerTemplate = (data) => {
    return (
      <div className="flex align-items-center gap-2 justify-content-between">
        <span className="font-bold">{data.current?.unvan}</span>
        <div className="flex justify-content-end font-bold ">
          Toplam Kullanıcı: {calculateCustomerTotal(data.current?.unvan)}
        </div>
      </div>
    );
  };

  const calculateCustomerTotal = (name) =>
    users?.filter((user) => user.current?.unvan === name).length || 0;

  const groupBodyTemplate = (rowData) => {
    let a = "Yetkisiz";
    if (rowData.groups) {
      if (rowData.groups[0]?.id === 1) {
        a = "Sistem Admin";
      } else if (rowData.groups[0]?.id === 2) {
        a = "Cari Admin";
      } else {
        a = "Cari Kullanıcı";
      }
    }
    return <span>{a}</span>;
  };

  const footer = (
    <div className=" flex justify-content-between">
      <span>Kullanıcı Sayısı: {users ? users?.length : 0}</span>
      <div>
        <span className="ml-3">
          Aktif: {users?.filter((u) => u.status).length}
        </span>
        <span className="ml-3">
          Pasif: {users?.filter((u) => !u.status).length}
        </span>
      </div>
    </div>
  );

  const [filters, setFilters] = useState(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  useEffect(() => {
    initFilters();
  }, []);

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setGlobalFilterValue("");
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setFilters((prevFilters) => ({
      ...prevFilters,
      global: { value, matchMode: FilterMatchMode.CONTAINS },
    }));
    setGlobalFilterValue(value);
  };

  const clearFilter = () => {
    initFilters();
  };

  const renderHeader = () => (
    <div className="flex justify-content-between  gap-2 ">
      <IconField iconPosition="left" className="w-12">
        <InputIcon className="pi pi-search" />
        <InputText
          className="w-full"
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder="Cari Ara"
        />
      </IconField>
      <Button
        className={`${!globalFilterValue && "hidden"} `}
        icon="pi pi-filter-slash"
        text
        outlined
        onClick={clearFilter}
      />
    </div>
  );
  const header = renderHeader();
  return (
    <div>
      <Toolbar
        className="shadow-3 border-round-md  my-2"
        start={<div className="text-2xl font-bold">Kullanıcı Ayarları</div>}
        end={
          <Button
            label="Yeni Kullanıcı"
            icon="pi pi-user-plus"
            onClick={() => openDialog()}
          />
        }
      />
      <DataTable
        className="shadow-3 border-round-md table-content surface-ground"
        value={users}
        loading={isLoading}
        footer={footer}
        emptyMessage={
          isLoading ? "Veriler işleniyor, lütfen bekleyin..." : "Liste Boş"
        }
        rowGroupHeaderTemplate={headerTemplate}
        rowGroupMode="subheader"
        groupRowsBy="current.unvan"
        sortMode="single"
        sortField="current.unvan"
        sortOrder={1}
        scrollable
        scrollHeight="flex"
        dataKey="userId"
        filters={filters}
        filterDisplay="menu"
        globalFilterFields={["current.unvan", "email", "fullName"]}
        header={header}
        onFilter={(e) => setFilters(e.filters)}
      >
        <Column field="userId" header="ID" />

        <Column field="fullName" header="Ad Soyad" />
        <Column field="email" header="Email" />
        <Column field="address" header="Adres" />
        <Column field="mobilePhones" header="Telefon" />
        <Column field="notes" header="Not" />

        <Column field="status" header="Durum" body={statusBodyTemplate} />
        <Column
          field="groups.groupName"
          header="Yetki"
          body={groupBodyTemplate}
        />
        <Column
          header="İşlemler"
          alignHeader="center"
          align="center"
          headerStyle={{ width: "4rem", textAlign: "center" }}
          bodyStyle={{ textAlign: "center", overflow: "visible" }}
          body={actionBodyTemplate}
        />
      </DataTable>
      <CompanyForm
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={(data) => mutation.mutate(data)}
        initialData={selectedUser}
        currents={currents}
        users={users}
      />
      <ChangePasswordForm
        isOpen={passwordDialog}
        onClose={closePasswordDialog}
        onSubmit={(data) => passwordMutation.mutate(data)}
        initialData={selectedUser}
      />
      <UserClaimForm
        isOpen={claimDialog}
        onClose={closeClaimDialog}
        onSubmit={(data) => claimMutation.mutate(data)}
        initialData={selectedUser}
      />
      <PasswordDialog
        isOpen={passInfoDialog}
        onClose={closePassInfoDialog}
        initialData={selectedUser}
      />
      <ConfirmDialog />
    </div>
  );
}
